import React from 'react';
import { OrchestratedLadderDetails } from '../api/tradeGroups';
import './shared/MetricCards.css';
import './shared/DataTable.css';
import './OrdersTable.css';

interface OrchestratedLadderSectionProps {
  orchestratedLadder?: OrchestratedLadderDetails | null;
}

/**
 * Detail-surfacing panel for a Trade Group Orchestrator (ORCHESTRATED_LADDER) trade
 * group (TASK-032). Reads the nested `orchestratedLadder` sub-object projected by
 * `TradeGroupApiHandler.mapOrchestratedLadderRow` and renders the lifecycle FSM state,
 * the managed child ladder id, the aggregate realized/unrealized PnL (decimal fractions
 * of the allocation, rendered ×100 as a percent — the display-only ×100 belongs here),
 * the last confluence gate score, and the deployed-config snapshot.
 *
 * Mirrors the sibling Ichimoku detail block's `position-detail-card` markup and the
 * VolatilityAttributesSection extraction pattern (own formatters, dark-theme styling).
 * Returns null when the sub-object is absent so a group without it renders no section.
 */
const OrchestratedLadderSection: React.FC<OrchestratedLadderSectionProps> = ({
  orchestratedLadder,
}) => {
  if (!orchestratedLadder) {
    return null;
  }

  const formatCurrency = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Decimal fraction → percent for display only (allowed front-end ×100).
  const formatPercent = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return 'N/A';
    return `${(value * 100).toFixed(2)}%`;
  };

  const formatDateTime = (timestamp: number | null | undefined): string => {
    if (timestamp === null || timestamp === undefined) return 'N/A';
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    });
  };

  // Sign-driven PnL cell class, reusing the shared .pnl-positive/.pnl-negative chrome.
  const pnlClass = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '';
    return value >= 0 ? 'pnl-positive' : 'pnl-negative';
  };

  const {
    orchestratorState,
    deployedChildTradeGroupId,
    lastConfluenceScore,
    aggregateRealizedPnl,
    aggregateUnrealizedPnl,
    pnlHighWaterMark,
    deployedMinPrice,
    deployedMaxPrice,
    deployedNumRungs,
    deployedQuoteDistributionType,
    deployedCapitalAllocation,
    cycles,
  } = orchestratedLadder;

  return (
    <>
      {/* Orchestrator lifecycle + managed child */}
      <div className="position-details-grid">
        <div className="position-detail-card">
          <span className="detail-label">Orchestrator State</span>
          <span className="detail-value">
            {orchestratorState ? orchestratorState.replace(/_/g, ' ') : 'N/A'}
          </span>
        </div>
        <div className="position-detail-card">
          <span className="detail-label">Managed Child Trade Group</span>
          <span className="detail-value">{deployedChildTradeGroupId ?? 'N/A'}</span>
        </div>
        <div className="position-detail-card">
          <span className="detail-label">Last Confluence Score</span>
          <span className="detail-value">{formatPercent(lastConfluenceScore)}</span>
        </div>
      </div>

      {/* Aggregate PnL (decimal fractions of allocation) */}
      <h3 className="section-title">Aggregate P&amp;L</h3>
      <div className="position-details-grid">
        <div className="position-detail-card">
          <span className="detail-label">Realized P&amp;L</span>
          <span className="detail-value">{formatPercent(aggregateRealizedPnl)}</span>
        </div>
        <div className="position-detail-card">
          <span className="detail-label">Unrealized P&amp;L</span>
          <span className="detail-value">{formatPercent(aggregateUnrealizedPnl)}</span>
        </div>
        <div className="position-detail-card">
          <span className="detail-label">P&amp;L High-Water Mark</span>
          <span className="detail-value">{formatPercent(pnlHighWaterMark)}</span>
        </div>
      </div>

      {/* Deployed-config snapshot */}
      <h3 className="section-title">Deployed Configuration</h3>
      <div className="position-details-grid">
        <div className="position-detail-card">
          <span className="detail-label">Price Range</span>
          <span className="detail-value">
            {formatCurrency(deployedMinPrice)} &ndash; {formatCurrency(deployedMaxPrice)}
          </span>
        </div>
        <div className="position-detail-card">
          <span className="detail-label">Number of Rungs</span>
          <span className="detail-value">{deployedNumRungs ?? 'N/A'}</span>
        </div>
        <div className="position-detail-card">
          <span className="detail-label">Distribution Type</span>
          <span className="detail-value">
            {deployedQuoteDistributionType
              ? deployedQuoteDistributionType.replace(/_/g, ' ')
              : 'N/A'}
          </span>
        </div>
        <div className="position-detail-card">
          <span className="detail-label">Capital Allocation</span>
          <span className="detail-value">{formatCurrency(deployedCapitalAllocation)}</span>
        </div>
      </div>

      {/* Per-cycle deployment history (one row per orchestrator cycle, newest first). */}
      {cycles && cycles.length > 0 && (
        <>
          <h3 className="section-title">Cycle History</h3>
          <table className="data-table" aria-label="Orchestrator cycle history">
            <thead>
              <tr>
                <th>Cycle</th>
                <th>State</th>
                <th>Managed Child</th>
                <th>Confluence</th>
                <th>Deployed</th>
                <th>Exit Reason</th>
                <th>Exited</th>
                <th>Realized P&amp;L</th>
                <th>Unrealized P&amp;L</th>
              </tr>
            </thead>
            <tbody>
              {cycles.map((cycle) => (
                <tr key={cycle.cycleNumber}>
                  <td>{cycle.cycleNumber}</td>
                  <td>{cycle.cycleState ? cycle.cycleState.replace(/_/g, ' ') : 'N/A'}</td>
                  <td>{cycle.deployedChildTradeGroupId ?? 'N/A'}</td>
                  <td>{formatPercent(cycle.confluenceScore)}</td>
                  <td>{formatDateTime(cycle.deploymentTime)}</td>
                  <td>{cycle.exitReason ? cycle.exitReason.replace(/_/g, ' ') : 'N/A'}</td>
                  <td>{formatDateTime(cycle.exitTime)}</td>
                  <td className={pnlClass(cycle.aggregateRealizedPnl)}>
                    {formatPercent(cycle.aggregateRealizedPnl)}
                  </td>
                  <td className={pnlClass(cycle.aggregateUnrealizedPnl)}>
                    {formatPercent(cycle.aggregateUnrealizedPnl)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </>
  );
};

export default OrchestratedLadderSection;
